import { useState } from 'react';
import PropTypes from 'prop-types';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import { MONTH_NAMES, unitColor } from '@/utils/gconUtils';

const ByUnitView = ({ data, reportingYears }) => {
  const [hoveredUnit, setHoveredUnit] = useState(null);
  const [levelized, setLevelized] = useState(false);

  if (!data?.units?.length) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-2">
        <ChartBarIcon className="w-10 h-10" />
        <p className="text-sm">No unit data available.</p>
      </div>
    );
  }

  const { months, units } = data;
  const multiYear = reportingYears?.length > 1;
  const hasProduction = units.some(u => (u.production ?? []).some(v => v > 0));

  const fmtMonth = key => {
    const [y, m] = key.split('-');
    const label = MONTH_NAMES[parseInt(m) - 1];
    return multiYear && m === '01' ? `${label} '${y.slice(2)}` : label;
  };

  // When levelized: CO₂e / production for that unit that month
  const getVal = (u, mi) => {
    const raw = u.values[mi] ?? 0;
    if (!levelized) return raw;
    const prod = u.production?.[mi] ?? 0;
    return prod > 0 ? raw / prod : 0;
  };

  // Levelized display is in kg CO₂e / t (multiply t/t by 1000) to keep values readable
  const fmtVal = v => {
    if (levelized) {
      const kg = v * 1000;
      if (kg >= 10000) return `${(kg / 1000).toFixed(1)}k`;
      if (kg >= 100) return kg.toFixed(0);
      if (kg >= 10) return kg.toFixed(1);
      return kg.toFixed(2);
    }
    return v >= 1_000_000 ? `${(v / 1_000_000).toFixed(1)}M` : v >= 1_000 ? `${(v / 1_000).toFixed(0)}k` : v.toFixed(0);
  };

  // Aggregate intensity has to be production-weighted (total CO₂e / total
  // production). Averaging the monthly ratios would give a low-output month the
  // same weight as a peak month, and would disagree with the B.O.P. tab.
  const unitCo2e = u => months.reduce((s, _, mi) => s + (u.values?.[mi] ?? 0), 0);
  const unitProd = u => months.reduce((s, _, mi) => s + (u.production?.[mi] ?? 0), 0);
  const unitAvgIntensity = u => {
    const p = unitProd(u);
    return p > 0 ? unitCo2e(u) / p : 0;
  };
  const unitMetric = u => (levelized ? unitAvgIntensity(u) : unitCo2e(u));
  // Share always means share of emissions — summing intensity ratios is meaningless.
  const totalCo2eAll = units.reduce((s, u) => s + unitCo2e(u), 0);

  const monthTotals = months.map((_, mi) => units.reduce((sum, u) => sum + getVal(u, mi), 0));
  const maxTotal = Math.max(...monthTotals, 0.001);
  const peakIdx = monthTotals.indexOf(Math.max(...monthTotals));
  const topUnit = [...units].sort((a, b) => unitMetric(b) - unitMetric(a))[0];

  // SVG chart
  const VW = 820,
    VH = 210,
    ML = 62,
    MB = multiYear ? 36 : 28,
    MT = 8;
  const chartW = VW - ML - 8;
  const chartH = VH - MB - MT;
  const n = months.length;
  const barW = Math.max(chartW / n - 2, 3);

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map(f => ({
    val: maxTotal * f,
    y: VH - MB - f * chartH,
  }));

  return (
    <div className="p-6 overflow-y-auto h-full space-y-5">
      {/* KPI strip */}
      <div className="flex gap-3">
        {[
          {
            label: levelized ? 'Highest intensity' : 'Top unit',
            value: topUnit?.location || '—',
            sub: levelized
              ? `${fmtVal(unitAvgIntensity(topUnit))} kg CO₂e/t average`
              : `${totalCo2eAll > 0 ? ((unitCo2e(topUnit) / totalCo2eAll) * 100).toFixed(1) : 0}% of total`,
          },
          {
            label: 'Peak month',
            value: months[peakIdx] ? fmtMonth(months[peakIdx]) : '—',
            sub: fmtVal(monthTotals[peakIdx] ?? 0) + (levelized ? ' kg CO₂e/t' : ' t CO₂e'),
          },
          { label: 'Units tracked', value: units.length, sub: `${months.length} months` },
        ].map(s => (
          <div key={s.label} className="flex-1 bg-white border border-gray-100 rounded-lg px-4 py-3">
            <p className="text-xs text-gray-400 mb-0.5">{s.label}</p>
            <p className="text-sm font-semibold text-gray-800">{s.value}</p>
            <p className="text-xs text-gray-400">{s.sub}</p>
          </div>
        ))}
      </div>

      {/* Stacked bar chart */}
      <div className="bg-white border border-gray-100 rounded-lg p-4">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
            {levelized ? 'Monthly Emission Intensity by Unit (kg CO₂e / t product)' : 'Monthly CO₂e by Unit'}
          </p>
          {hasProduction && (
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <span className="text-xs text-gray-500">Levelized by production</span>
              <input
                type="checkbox"
                className="toggle toggle-sm toggle-primary"
                checked={levelized}
                onChange={e => setLevelized(e.target.checked)}
              />
            </label>
          )}
        </div>

        <svg viewBox={`0 0 ${VW} ${VH}`} className="w-full" style={{ height: VH }}>
          {yTicks.map(({ val, y }) => (
            <g key={y}>
              <line x1={ML} x2={VW - 8} y1={y} y2={y} stroke="#f0f0f0" strokeWidth="1" />
              <text x={ML - 4} y={y + 4} fontSize="9" fill="#9ca3af" textAnchor="end">
                {fmtVal(val)}
              </text>
            </g>
          ))}

          {months.map((month, mi) => {
            const bx = ML + (mi / n) * chartW + (chartW / n - barW) / 2;
            let cumH = 0;
            return (
              <g key={month}>
                {[...units].reverse().map(u => {
                  const val = getVal(u, mi);
                  if (val <= 0) return null;
                  const segH = (val / maxTotal) * chartH;
                  const sy = VH - MB - cumH - segH;
                  cumH += segH;
                  const dimmed = hoveredUnit && hoveredUnit !== u.location;
                  return (
                    <rect
                      key={u.location}
                      x={bx}
                      y={sy}
                      width={barW}
                      height={segH}
                      fill={unitColor(u.location)}
                      opacity={dimmed ? 0.2 : 0.88}
                      rx="1"
                    >
                      <title>{`${u.location} · ${fmtMonth(month)}: ${fmtVal(val)}${levelized ? ' kg CO₂e/t' : ' t CO₂e'}`}</title>
                    </rect>
                  );
                })}
                <text x={bx + barW / 2} y={VH - MB + 12} fontSize="8.5" fill="#6b7280" textAnchor="middle">
                  {fmtMonth(month)}
                </text>
                {multiYear && month.endsWith('-01') && (
                  <line
                    x1={bx - 1}
                    x2={bx - 1}
                    y1={MT}
                    y2={VH - MB}
                    stroke="#e5e7eb"
                    strokeWidth="1"
                    strokeDasharray="3 3"
                  />
                )}
              </g>
            );
          })}

          <line x1={ML} x2={ML} y1={MT} y2={VH - MB} stroke="#e5e7eb" strokeWidth="1" />
          <line x1={ML} x2={VW - 8} y1={VH - MB} y2={VH - MB} stroke="#e5e7eb" strokeWidth="1" />
          <text
            transform={`rotate(-90) translate(-${(VH - MB + MT) / 2 + MT}, 12)`}
            fontSize="9"
            fill="#9ca3af"
            textAnchor="middle"
          >
            {levelized ? 'kg CO₂e / t' : 't CO₂e'}
          </text>
        </svg>

        {/* Legend */}
        <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
          {units.map(u => (
            <button
              key={u.location}
              type="button"
              className="flex items-center gap-1.5 text-xs"
              onMouseEnter={() => setHoveredUnit(u.location)}
              onMouseLeave={() => setHoveredUnit(null)}
            >
              <div className="w-3 h-3 rounded-sm flex-shrink-0" style={{ background: unitColor(u.location) }} />
              <span className={hoveredUnit === u.location ? 'font-semibold text-gray-900' : 'text-gray-600'}>
                {u.location || 'Unknown'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Summary table */}
      <div className="bg-white border border-gray-100 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-50 border-b border-gray-100">
            <tr>
              <th className="text-left px-4 py-2.5 text-gray-500 font-medium">Unit</th>
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">
                {levelized ? 'Avg Intensity' : 'Total CO₂e'}
              </th>
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">
                {levelized ? 'Peak Intensity' : 'Avg / month'}
              </th>
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Peak month</th>
              <th className="text-right px-4 py-2.5 text-gray-500 font-medium">Share of CO₂e</th>
            </tr>
          </thead>
          <tbody>
            {[...units]
              .sort((a, b) => unitMetric(b) - unitMetric(a))
              .map(u => {
                const uVals = months.map((_, mi) => getVal(u, mi));
                const uTotal = uVals.reduce((s, v) => s + v, 0);
                const nonZero = uVals.filter(v => v > 0);
                // Weighted when levelized; a plain per-month mean otherwise
                const avg = levelized ? unitAvgIntensity(u) : nonZero.length ? uTotal / nonZero.length : 0;
                const peakVal = Math.max(...uVals, 0);
                const peakMonthKey = months[uVals.indexOf(peakVal)];
                const pct = totalCo2eAll > 0 ? (unitCo2e(u) / totalCo2eAll) * 100 : 0;
                return (
                  <tr key={u.location} className="border-b border-gray-50 hover:bg-gray-50">
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                          style={{ background: unitColor(u.location) }}
                        />
                        <span className="font-medium text-gray-700">{u.location || 'Unknown'}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-gray-700">
                      {fmtVal(levelized ? avg : uTotal)}
                      {levelized ? ' kg/t' : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-500">
                      {fmtVal(levelized ? peakVal : avg)}
                      {levelized ? ' kg/t' : ''}
                    </td>
                    <td className="px-4 py-2.5 text-right text-gray-500">
                      {peakMonthKey ? fmtMonth(peakMonthKey) : '—'}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        <div className="w-16 bg-gray-100 rounded-full h-1.5">
                          <div
                            className="h-1.5 rounded-full"
                            style={{ width: `${pct}%`, background: unitColor(u.location) }}
                          />
                        </div>
                        <span className="text-gray-600 w-9 text-right">{pct.toFixed(1)}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Best Operating Plant (B.O.P.) view
// ---------------------------------------------------------------------------

ByUnitView.propTypes = {
  data: PropTypes.object,
  reportingYears: PropTypes.arrayOf(PropTypes.number),
};

export default ByUnitView;
